import arrayLength from "@-/com.npmjs.extra-array__length";
import {
  type CurriculumDataset,
  type CurriculumGradeParts,
  type Level,
  getCurriculumMaterialTabDescription,
  levelMatchesCurriculumMaterialTab,
} from "@-/dev.improve.haniverse.lib.level";

export interface LevelsPackDescription {
  /** Markdown. */
  description: string;
  /**
   * Character-surface lessons plus pronunciation-surface lessons (N + M). Each is one surface
   * square: a card plus any slides that lead into or follow it.
   */
  lessonCount: number;
  levelCount: number;
  /** The exam levels the pack covers ("HSK 2–4"); null when the title already names them. */
  scope: string | null;
  /** "Ready Pack". */
  title: string;
}

/**
 * What a Browse → Levels pack teaches and how much of it there is: the whole pack, whatever is
 * locked, completed or filtered out. Null for a tab that is not a curriculum pack.
 */
export function getLevelsPackDescription({
  dataset,
  gradeParts,
  levels,
  tabValue,
}: {
  dataset: CurriculumDataset;
  gradeParts: CurriculumGradeParts;
  levels: readonly Level[];
  tabValue: number;
}): LevelsPackDescription | null {
  const description = getCurriculumMaterialTabDescription(tabValue, { dataset });
  if (description === null) {
    return null;
  }
  const packLevels = levels.filter((level) =>
    levelMatchesCurriculumMaterialTab(level, tabValue, { dataset, gradeParts }),
  );
  // N character-surface lessons + M pronunciation-surface lessons (each square = one lesson: a card
  // plus any slides that lead into or follow it). Words authored on a P-level count as lessons too,
  // at the end of that same level.
  let lessonCount = 0;
  for (const level of packLevels) {
    const pronunciationCharacters = level.pronunciationCharacters ?? [];
    const trailingWords =
      pronunciationCharacters.length === 0 ? [] : (level.pronunciationWords ?? []);
    lessonCount +=
      arrayLength(level.characters) +
      arrayLength(pronunciationCharacters) +
      arrayLength(trailingWords);
  }
  return { ...description, lessonCount, levelCount: arrayLength(packLevels) };
}
